import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/utils';

export type TooltipSide = 'top' | 'bottom' | 'left' | 'right';

export interface TooltipProps {
  /** Text shown on hover/focus. Also becomes the trigger's accessible name. */
  label: string;
  children: React.ReactElement<React.HTMLAttributes<HTMLElement> & { 'aria-label'?: string }>;
  side?: TooltipSide;
  /** Milliseconds of hover before showing. Focus shows immediately. */
  delayMs?: number;
  /** Wrap long text in a fixed-width box instead of one line. */
  multiline?: boolean;
  className?: string;
}

const GAP = 6;

function placement(side: TooltipSide, rect: DOMRect): React.CSSProperties {
  switch (side) {
    case 'top':
      return { top: rect.top - GAP, left: rect.left + rect.width / 2, transform: 'translate(-50%, -100%)' };
    case 'bottom':
      return { top: rect.bottom + GAP, left: rect.left + rect.width / 2, transform: 'translateX(-50%)' };
    case 'left':
      return { top: rect.top + rect.height / 2, left: rect.left - GAP, transform: 'translate(-100%, -50%)' };
    case 'right':
      return { top: rect.top + rect.height / 2, left: rect.right + GAP, transform: 'translateY(-50%)' };
  }
}

/**
 * Hover/focus label for a control whose meaning is otherwise only an icon.
 *
 * Rendered in the DOM rather than via the native `title` attribute: the Electron
 * title bar is a `-webkit-app-region: drag` surface, and native tooltips are not
 * delivered there even though the buttons themselves opt back out with
 * `no-drag`, so every icon in the app chrome was unexplained on hover.
 */
export function Tooltip({
  label,
  children,
  side = 'bottom',
  delayMs = 300,
  multiline = false,
  className,
}: TooltipProps) {
  const [anchor, setAnchor] = React.useState<DOMRect | null>(null);
  const wrapperRef = React.useRef<HTMLSpanElement>(null);
  const open = anchor !== null;
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = React.useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  React.useEffect(() => clear, [clear]);

  const show = React.useCallback(
    (immediate: boolean) => {
      clear();
      if (immediate) {
        setAnchor(wrapperRef.current?.getBoundingClientRect() ?? null);
        return;
      }
      timerRef.current = setTimeout(() => setAnchor(wrapperRef.current?.getBoundingClientRect() ?? null), delayMs);
    },
    [clear, delayMs],
  );

  const hide = React.useCallback(() => {
    clear();
    setAnchor(null);
  }, [clear]);

  const childProps = children.props;
  const child = React.cloneElement(children, {
    'aria-label': childProps['aria-label'] ?? label,
    onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
      childProps.onMouseEnter?.(e);
      show(false);
    },
    onMouseLeave: (e: React.MouseEvent<HTMLElement>) => {
      childProps.onMouseLeave?.(e);
      hide();
    },
    onFocus: (e: React.FocusEvent<HTMLElement>) => {
      childProps.onFocus?.(e);
      show(true);
    },
    onBlur: (e: React.FocusEvent<HTMLElement>) => {
      childProps.onBlur?.(e);
      hide();
    },
    onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
      childProps.onKeyDown?.(e);
      if (e.key === 'Escape') hide();
    },
  });

  return (
    <span ref={wrapperRef} className={cn('inline-flex', className)}>
      {child}
      {open &&
        anchor &&
        createPortal(
          <span
            role="tooltip"
            data-testid="tooltip"
            className={cn(
              'pointer-events-none fixed z-[100] rounded-md border bg-popover px-2 py-1',
              'text-caption text-popover-foreground shadow-md',
              multiline ? 'w-64 whitespace-normal leading-snug' : 'whitespace-nowrap',
            )}
            style={placement(side, anchor)}
          >
            {label}
          </span>,
          document.body,
        )}
    </span>
  );
}
